import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { QueryClient } from "@tanstack/react-query";
import { posApi } from "../../api/client";
import {
  getReferenceCache,
  getReferenceCacheStaleness,
  saveReferenceCacheBatch,
} from "./db";
import type {
  CachedCouponRuleRecord,
  CachedCustomerRecord,
  CachedEligibleCouponRecord,
  CachedLoyaltyRewardRecord,
  CouponRulesCache,
  CustomersByPhoneCache,
  EligibleCouponsCache,
  LoyaltyRewardsCache,
  ReferenceCacheManifest,
  ReferenceCacheRecord,
} from "./types";
import { KITCHEN_ROUTING_CACHE_SECTION } from "./kitchen-routing";
import {
  isAffordableLoyaltyRewardFromCache,
} from "./validate-loyalty-reward-from-cache";
import {
  calculateCouponDiscountFromSnapshot,
  type ServerCouponLike,
  toCachedCouponRuleRecord,
} from "./validate-coupon-from-cache";

const CACHE_VERSION = 1;
const DEFAULT_CACHE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const PRODUCT_PAGE_SIZE = 200;
export const CUSTOMERS_BY_PHONE_SECTION = "customers-by-phone";
export const COUPON_RULES_SECTION = "coupon-rules";
export const ELIGIBLE_COUPONS_SECTION = "eligible-coupons";
export const LOYALTY_REWARDS_SECTION = "loyalty-rewards";
export const KITCHEN_ROUTING_SECTION = KITCHEN_ROUTING_CACHE_SECTION;
export const MAX_CACHED_CUSTOMERS = 300;
export const MAX_CACHED_COUPON_RULES = 500;
export const MAX_CACHED_LOYALTY_REWARDS = 200;

const activeRefreshByBranch = new Map<
  string,
  Promise<ReferenceCacheManifest>
>();
const variantDetailsWriteChainByBranch = new Map<string, Promise<void>>();

export function isReferenceCacheRefreshInProgress(branchId: string): boolean {
  return activeRefreshByBranch.has(branchId);
}

export function isPosVariantDetailQueryKey(
  queryKey: readonly unknown[],
): boolean {
  if (queryKey[0] !== "pos") return false;
  return (
    queryKey.includes("variants") ||
    queryKey.includes("options") ||
    queryKey.includes("modifiers") ||
    queryKey.includes("find-variant")
  );
}

export async function invalidatePosReferenceQueries(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: ["pos"],
    predicate: (query) => !isPosVariantDetailQueryKey(query.queryKey),
  });
}

async function waitForVariantDetailsWrites(branchId: string): Promise<void> {
  const pending = variantDetailsWriteChainByBranch.get(branchId);
  if (pending) await pending.catch(() => {});
}

async function waitForReferenceCacheRefresh(branchId: string): Promise<void> {
  const pending = activeRefreshByBranch.get(branchId);
  if (pending) await pending.catch(() => {});
}

async function withVariantDetailsWriteLock<T>(
  branchId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const previous =
    variantDetailsWriteChainByBranch.get(branchId) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(fn);
  variantDetailsWriteChainByBranch.set(
    branchId,
    next.then(
      () => {},
      () => {},
    ),
  );
  return next;
}

type RefreshReferenceCacheInput = {
  branchId: string;
  deviceId?: string;
  maxAgeMs?: number;
  includeVariantDetails?: boolean;
  force?: boolean;
};

type CacheSection = {
  key: string;
  data: unknown;
  count: number;
};

type CacheFirstOptions = {
  deviceId?: string;
  maxAgeMs?: number;
  force?: boolean;
};

export function referenceCacheKey(branchId: string, section: string): string {
  return `branch:${branchId}:${section}`;
}

export function referenceCacheManifestKey(branchId: string): string {
  return referenceCacheKey(branchId, "manifest");
}

export async function getCachedReferenceData<TData>(
  branchId: string,
  section: string,
): Promise<TData | undefined> {
  const record = await getReferenceCache<TData>(
    referenceCacheKey(branchId, section),
  );
  return record?.data;
}

export async function getCachedReferenceManifest(
  branchId: string,
): Promise<ReferenceCacheManifest | undefined> {
  const record = await getReferenceCache<ReferenceCacheManifest>(
    referenceCacheManifestKey(branchId),
  );
  return record?.data;
}

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

function countValue(data: unknown): number {
  return Array.isArray(data) ? data.length : 1;
}

export async function saveReferenceCacheSection<TData>(
  branchId: string,
  section: string,
  data: TData,
  options: CacheFirstOptions = {},
): Promise<void> {
  const cachedAt = new Date().toISOString();
  const maxAgeMs = options.maxAgeMs ?? DEFAULT_CACHE_MAX_AGE_MS;
  const expiresAt = new Date(Date.now() + maxAgeMs).toISOString();
  const key = referenceCacheKey(branchId, section);
  const existingManifest = await getCachedReferenceManifest(branchId);
  const keys = Array.from(new Set([...(existingManifest?.keys ?? []), key]));
  const counts = {
    ...(existingManifest?.counts ?? {}),
    [key]: countValue(data),
  };
  const manifest: ReferenceCacheManifest = {
    branchId,
    deviceId: options.deviceId ?? existingManifest?.deviceId,
    refreshedAt: cachedAt,
    cacheVersion: CACHE_VERSION,
    keys,
    counts,
    errors: existingManifest?.errors ?? [],
  };

  await saveReferenceCacheBatch([
    {
      key,
      branchId,
      deviceId: options.deviceId,
      data,
      cacheVersion: CACHE_VERSION,
      cachedAt,
      expiresAt,
    },
    {
      key: referenceCacheManifestKey(branchId),
      branchId,
      deviceId: options.deviceId,
      data: manifest,
      cacheVersion: CACHE_VERSION,
      cachedAt,
      expiresAt,
    },
  ]);
}

async function refreshCacheSection<TData>(
  branchId: string,
  section: string,
  loader: () => Promise<TData>,
  options: CacheFirstOptions = {},
): Promise<TData> {
  const data = await loader();
  await saveReferenceCacheSection(branchId, section, data, options);
  return data;
}

async function getOrFetchReferenceSection<TData>(
  branchId: string,
  section: string,
  loader: () => Promise<TData>,
  options: CacheFirstOptions = {},
): Promise<TData> {
  const cached = await getCachedReferenceData<TData>(branchId, section);
  if (cached !== undefined && !options.force) {
    const staleness = await getReferenceCacheStaleness(
      referenceCacheKey(branchId, section),
      options.maxAgeMs ?? DEFAULT_CACHE_MAX_AGE_MS,
    );
    if (staleness.stale && canUseNetwork()) {
      void refreshCacheSection(branchId, section, loader, options).catch(
        (error) => {
          console.warn("[reference-cache] background refresh failed", {
            branchId,
            section,
            error,
          });
        },
      );
    }
    return cached;
  }

  return await refreshCacheSection(branchId, section, loader, options);
}

export async function getCachedBranchProductsPage<
  TProduct extends { categoryId: string | null; name: string },
>(
  branchId: string,
  params: { categoryId?: string | null; limit: number; offset: number },
): Promise<{ data: TProduct[]; total: number } | undefined> {
  const products = await getCachedReferenceData<TProduct[]>(
    branchId,
    "products",
  );
  if (!products) return undefined;

  const filtered = params.categoryId
    ? products.filter((product) => product.categoryId === params.categoryId)
    : products;
  return {
    data: filtered.slice(params.offset, params.offset + params.limit),
    total: filtered.length,
  };
}

export async function getOrFetchCategories<TCategory>(
  branchId: string,
  options?: CacheFirstOptions,
): Promise<TCategory[]> {
  return await getOrFetchReferenceSection(
    branchId,
    "categories",
    () => posApi.getCategories(branchId) as Promise<TCategory[]>,
    options,
  );
}

export async function getOrFetchPaymentMethods<TPaymentMethod>(
  branchId: string,
  options?: CacheFirstOptions,
): Promise<TPaymentMethod[]> {
  return await getOrFetchReferenceSection(
    branchId,
    "payment-methods",
    () => posApi.getPaymentMethods(branchId) as Promise<TPaymentMethod[]>,
    options,
  );
}

export async function getOrFetchTaxSettings<TTaxSettings>(
  branchId: string,
  options?: CacheFirstOptions,
): Promise<TTaxSettings> {
  return await getOrFetchReferenceSection(
    branchId,
    "tax-settings",
    () => posApi.getBranchTaxSettings(branchId) as Promise<TTaxSettings>,
    options,
  );
}

export async function getOrFetchServiceChargePresets<TPreset>(
  branchId: string,
  options?: CacheFirstOptions,
): Promise<TPreset[]> {
  return await getOrFetchReferenceSection(
    branchId,
    "service-charge-presets",
    () => posApi.getServiceChargePresets(branchId) as Promise<TPreset[]>,
    options,
  );
}

export async function getOrFetchDeliveryPartners<TDeliveryPartner>(
  branchId: string,
  options?: CacheFirstOptions,
): Promise<TDeliveryPartner[]> {
  return await getOrFetchReferenceSection(
    branchId,
    "delivery-partners",
    () => posApi.getDeliveryPartners(branchId) as Promise<TDeliveryPartner[]>,
    options,
  );
}

export async function getOrFetchBranchProductsPage<
  TProduct extends { categoryId: string | null; name: string },
>(
  branchId: string,
  params: { categoryId?: string | null; limit: number; offset: number },
  options?: CacheFirstOptions,
): Promise<{ data: TProduct[]; total: number }> {
  const cached = await getCachedBranchProductsPage<TProduct>(branchId, params);
  if (cached && !options?.force) {
    const staleness = await getReferenceCacheStaleness(
      referenceCacheKey(branchId, "products"),
      options?.maxAgeMs ?? DEFAULT_CACHE_MAX_AGE_MS,
    );
    if (staleness.stale && canUseNetwork()) {
      void refreshCacheSection(
        branchId,
        "products",
        () => fetchAllBranchProducts(branchId),
        options,
      ).catch((error) => {
        console.warn("[reference-cache] products refresh failed", error);
      });
    }
    return cached;
  }

  const products = (await refreshCacheSection(
    branchId,
    "products",
    () => fetchAllBranchProducts(branchId),
    options,
  )) as unknown as TProduct[];
  const filtered = params.categoryId
    ? products.filter((product) => product.categoryId === params.categoryId)
    : products;
  return {
    data: filtered.slice(params.offset, params.offset + params.limit),
    total: filtered.length,
  };
}

export async function searchCachedBranchProducts<
  TProduct extends { name: string },
>(branchId: string, query: string): Promise<TProduct[] | undefined> {
  const products = await getCachedReferenceData<TProduct[]>(
    branchId,
    "products",
  );
  if (!products) return undefined;

  const normalizedQuery = query.trim().toLowerCase();
  return products.filter((product) =>
    product.name.toLowerCase().includes(normalizedQuery),
  );
}

export async function searchOrFetchBranchProducts<
  TProduct extends { name: string },
>(branchId: string, query: string): Promise<TProduct[]> {
  const cached = await searchCachedBranchProducts<TProduct>(branchId, query);
  if (cached) return cached;
  return (await posApi.searchProducts(
    branchId,
    query,
  )) as unknown as TProduct[];
}

type CachedVariantDetails = {
  productVariants: Record<string, unknown>;
  productOptions: Record<string, unknown>;
  variantModifiers: Record<string, unknown>;
};

export async function getCachedProductVariants<TVariant>(
  branchId: string,
  productId: string,
): Promise<TVariant[] | undefined> {
  await waitForVariantDetailsWrites(branchId);
  const details = await getCachedReferenceData<CachedVariantDetails>(
    branchId,
    "variant-details",
  );
  if (!details || !(productId in details.productVariants)) return undefined;
  const variants = details.productVariants[productId];
  return Array.isArray(variants) ? (variants as TVariant[]) : undefined;
}

async function readCachedVariantDetails(
  branchId: string,
): Promise<CachedVariantDetails> {
  return (
    (await getCachedReferenceData<CachedVariantDetails>(
      branchId,
      "variant-details",
    )) ?? {
      productVariants: {},
      productOptions: {},
      variantModifiers: {},
    }
  );
}

export async function getOrFetchProductVariants<TVariant>(
  branchId: string,
  productId: string,
  options?: CacheFirstOptions,
): Promise<TVariant[]> {
  await waitForReferenceCacheRefresh(branchId);

  const cached = await getCachedProductVariants<TVariant>(branchId, productId);
  if (cached !== undefined && !options?.force) return cached;

  const variants = (await posApi.getProductVariants(
    productId,
    branchId,
  )) as TVariant[];
  await withVariantDetailsWriteLock(branchId, async () => {
    const details = await readCachedVariantDetails(branchId);
    details.productVariants[productId] = variants;
    await saveReferenceCacheSection(
      branchId,
      "variant-details",
      details,
      options,
    );
  });
  return variants;
}

export async function getCachedProductOptions<TOption>(
  branchId: string,
  productId: string,
): Promise<TOption[] | undefined> {
  await waitForVariantDetailsWrites(branchId);
  const details = await getCachedReferenceData<CachedVariantDetails>(
    branchId,
    "variant-details",
  );
  if (!details || !(productId in details.productOptions)) return undefined;
  const options = details.productOptions[productId];
  return Array.isArray(options) ? (options as TOption[]) : undefined;
}

export async function getOrFetchProductOptions<TOption>(
  branchId: string,
  productId: string,
  options?: CacheFirstOptions,
): Promise<TOption[]> {
  await waitForReferenceCacheRefresh(branchId);

  const cached = await getCachedProductOptions<TOption>(branchId, productId);
  if (cached !== undefined && !options?.force) return cached;

  const optionsData = (await posApi.getProductOptions(
    productId,
    branchId,
  )) as TOption[];
  await withVariantDetailsWriteLock(branchId, async () => {
    const details = await readCachedVariantDetails(branchId);
    details.productOptions[productId] = optionsData;
    await saveReferenceCacheSection(
      branchId,
      "variant-details",
      details,
      options,
    );
  });
  return optionsData;
}

export async function getCachedVariantModifiers<TModifier>(
  branchId: string,
  variantId: string,
): Promise<TModifier[] | undefined> {
  await waitForVariantDetailsWrites(branchId);
  const details = await getCachedReferenceData<CachedVariantDetails>(
    branchId,
    "variant-details",
  );
  if (!details || !(variantId in details.variantModifiers)) return undefined;
  const modifiers = details.variantModifiers[variantId];
  return Array.isArray(modifiers) ? (modifiers as TModifier[]) : undefined;
}

export async function getOrFetchVariantModifiers<TModifier>(
  branchId: string,
  variantId: string,
  options?: CacheFirstOptions,
): Promise<TModifier[]> {
  await waitForReferenceCacheRefresh(branchId);

  const cached = await getCachedVariantModifiers<TModifier>(
    branchId,
    variantId,
  );
  if (cached !== undefined && !options?.force) return cached;

  const modifiers = (await posApi.getVariantModifiers(
    variantId,
    branchId,
  )) as TModifier[];
  await withVariantDetailsWriteLock(branchId, async () => {
    const details = await readCachedVariantDetails(branchId);
    details.variantModifiers[variantId] = modifiers;
    await saveReferenceCacheSection(
      branchId,
      "variant-details",
      details,
      options,
    );
  });
  return modifiers;
}

type KitchenRoutingCacheEntry = {
  variantId: string;
  stationId: string;
  stationName: string;
  stationCode?: string | null;
};

const OPEN_SHIFT_SECTION = "open-shift";
const LATEST_SHIFT_SECTION = "latest-shift";

type CachedShiftEnvelope<TShift> = {
  shift: TShift | null;
  cachedAt: string;
};

export async function saveCachedOpenShift<TShift>(
  branchId: string,
  deviceId: string,
  shift: TShift | null,
): Promise<void> {
  const envelope: CachedShiftEnvelope<TShift> = {
    shift,
    cachedAt: new Date().toISOString(),
  };
  await saveReferenceCacheSection(
    branchId,
    `${OPEN_SHIFT_SECTION}:${deviceId}`,
    envelope,
    { deviceId },
  );
}

export async function getCachedOpenShift<TShift>(
  branchId: string,
  deviceId: string,
): Promise<TShift | null | undefined> {
  const envelope = await getCachedReferenceData<CachedShiftEnvelope<TShift>>(
    branchId,
    `${OPEN_SHIFT_SECTION}:${deviceId}`,
  );
  return envelope ? envelope.shift : undefined;
}

export async function saveCachedLatestShift<TShift>(
  branchId: string,
  shift: TShift | null,
): Promise<void> {
  const envelope: CachedShiftEnvelope<TShift> = {
    shift,
    cachedAt: new Date().toISOString(),
  };
  await saveReferenceCacheSection(branchId, LATEST_SHIFT_SECTION, envelope);
}

export async function getCachedLatestShift<TShift>(
  branchId: string,
): Promise<TShift | null | undefined> {
  const envelope = await getCachedReferenceData<CachedShiftEnvelope<TShift>>(
    branchId,
    LATEST_SHIFT_SECTION,
  );
  return envelope ? envelope.shift : undefined;
}

export async function getCachedKitchenRouting(
  branchId: string,
): Promise<KitchenRoutingCacheEntry[]> {
  const cached = await getCachedReferenceData<KitchenRoutingCacheEntry[]>(
    branchId,
    KITCHEN_ROUTING_SECTION,
  );
  return Array.isArray(cached) ? cached : [];
}

export async function getOrFetchKitchenRouting(
  branchId: string,
  options: CacheFirstOptions = {},
): Promise<KitchenRoutingCacheEntry[]> {
  return await getOrFetchReferenceSection<KitchenRoutingCacheEntry[]>(
    branchId,
    KITCHEN_ROUTING_SECTION,
    () => posApi.getKitchenRouting(branchId),
    options,
  );
}

async function fetchAllBranchProducts(branchId: string) {
  const firstPage = await posApi.getBranchProducts(
    branchId,
    null,
    PRODUCT_PAGE_SIZE,
    0,
  );
  const products = [...firstPage.data];
  for (
    let offset = PRODUCT_PAGE_SIZE;
    offset < firstPage.total;
    offset += PRODUCT_PAGE_SIZE
  ) {
    const page = await posApi.getBranchProducts(
      branchId,
      null,
      PRODUCT_PAGE_SIZE,
      offset,
    );
    products.push(...page.data);
  }
  return products;
}

async function captureSection<TData>(
  key: string,
  loader: () => Promise<TData>,
  count: (data: TData) => number,
  errors: ReferenceCacheManifest["errors"],
): Promise<CacheSection | null> {
  try {
    const data = await loader();
    return { key, data, count: count(data) };
  } catch (error) {
    errors.push({
      key,
      message: error instanceof Error ? error.message : "Unknown cache error",
    });
    return null;
  }
}

async function loadLogoDataUrl(logoUrl: string | null): Promise<string | null> {
  if (!logoUrl) return null;
  const resolvedUrl = resolveImageSrc(logoUrl);
  if (!resolvedUrl) return null;

  try {
    const res = await fetch(resolvedUrl, { credentials: "include" });
    if (!res.ok) return resolvedUrl;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return resolvedUrl;
  }
}

async function getBranchReceiptInfoForCache(branchId: string) {
  const receiptInfo = await posApi.getBranchReceiptInfo(branchId);
  const logoDataUrl = await loadLogoDataUrl(receiptInfo.billSettings.logoUrl);
  return {
    ...receiptInfo,
    billSettings: {
      ...receiptInfo.billSettings,
      logoDataUrl,
    },
  };
}

async function loadVariantDetails(
  branchId: string,
  productIds: string[],
): Promise<{
  productVariants: Record<string, unknown>;
  productOptions: Record<string, unknown>;
  variantModifiers: Record<string, unknown>;
}> {
  const productVariants: Record<string, unknown> = {};
  const productOptions: Record<string, unknown> = {};
  const variantModifiers: Record<string, unknown> = {};

  for (const productId of productIds) {
    const variants = await posApi.getProductVariants(productId, branchId);
    const options = await posApi.getProductOptions(productId, branchId);
    productVariants[productId] = variants;
    productOptions[productId] = options;

    for (const variant of variants) {
      const modifiers = await posApi.getVariantModifiers(variant.id, branchId);
      variantModifiers[variant.id] = modifiers;
    }
  }

  return { productVariants, productOptions, variantModifiers };
}

export async function refreshPosReferenceCache(
  input: RefreshReferenceCacheInput,
): Promise<ReferenceCacheManifest> {
  const existing = activeRefreshByBranch.get(input.branchId);
  if (existing && !input.force) {
    return existing;
  }
  if (existing && input.force) {
    await existing.catch(() => {});
  }

  const promise = refreshPosReferenceCacheInner(input).finally(() => {
    if (activeRefreshByBranch.get(input.branchId) === promise) {
      activeRefreshByBranch.delete(input.branchId);
    }
  });
  activeRefreshByBranch.set(input.branchId, promise);
  return promise;
}

async function refreshPosReferenceCacheInner({
  branchId,
  deviceId,
  maxAgeMs = DEFAULT_CACHE_MAX_AGE_MS,
  includeVariantDetails = false,
}: RefreshReferenceCacheInput): Promise<ReferenceCacheManifest> {
  const cachedAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + maxAgeMs).toISOString();
  const errors: ReferenceCacheManifest["errors"] = [];

  const sections: CacheSection[] = [];
  const baseSections = await Promise.all([
    captureSection(
      referenceCacheKey(branchId, "categories"),
      () => posApi.getCategories(branchId),
      (data) => data.length,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "products"),
      () => fetchAllBranchProducts(branchId),
      (data) => data.length,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "payment-methods"),
      () => posApi.getPaymentMethods(branchId),
      (data) => data.length,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "tax-settings"),
      () => posApi.getBranchTaxSettings(branchId),
      () => 1,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "service-charge-presets"),
      () => posApi.getServiceChargePresets(branchId),
      (data) => data.length,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "delivery-partners"),
      () => posApi.getDeliveryPartners(branchId),
      (data) => data.length,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, "branch-receipt-info"),
      () => getBranchReceiptInfoForCache(branchId),
      () => 1,
      errors,
    ),
    captureSection(
      referenceCacheKey(branchId, KITCHEN_ROUTING_SECTION),
      () => posApi.getKitchenRouting(branchId),
      (data) => data.length,
      errors,
    ),
  ]);

  for (const section of baseSections) {
    if (section) sections.push(section);
  }

  const existingCustomerCache =
    await getCachedReferenceData<CustomersByPhoneCache>(
      branchId,
      CUSTOMERS_BY_PHONE_SECTION,
    );
  if (existingCustomerCache) {
    sections.push({
      key: referenceCacheKey(branchId, CUSTOMERS_BY_PHONE_SECTION),
      data: existingCustomerCache,
      count: Object.keys(existingCustomerCache.customers).length,
    });
  }

  const existingCouponRulesCache =
    await getCachedReferenceData<CouponRulesCache>(
      branchId,
      COUPON_RULES_SECTION,
    );
  if (existingCouponRulesCache) {
    sections.push({
      key: referenceCacheKey(branchId, COUPON_RULES_SECTION),
      data: existingCouponRulesCache,
      count: Object.keys(existingCouponRulesCache.rules).length,
    });
  }

  if (canUseNetwork()) {
    const eligibleCouponsSection = await captureSection(
      referenceCacheKey(branchId, ELIGIBLE_COUPONS_SECTION),
      async () => {
        const response = await posApi.getEligibleCoupons(
          undefined,
          undefined,
          0,
        );
        const cachedAt = new Date().toISOString();
        const coupons: CachedEligibleCouponRecord[] = response.coupons.map(
          (coupon) => ({
            ...toCachedCouponRuleRecord(coupon, cachedAt),
            calculatedDiscount: coupon.calculatedDiscount,
          }),
        );

        await mergeCouponRulesIntoCache(branchId, response.coupons, {
          deviceId,
        });

        const eligibleCache: EligibleCouponsCache = {
          coupons,
          cachedAt,
        };
        return eligibleCache;
      },
      (data) => data.coupons.length,
      errors,
    );
    if (eligibleCouponsSection) sections.push(eligibleCouponsSection);

    const loyaltyRewardsSection = await captureSection(
      referenceCacheKey(branchId, LOYALTY_REWARDS_SECTION),
      async () => {
        const response = await posApi.getEligibleLoyaltyRewards("", false);
        const cachedAt = new Date().toISOString();
        const rewards: Record<string, CachedLoyaltyRewardRecord> = {};
        const recentRewardIds: string[] = [];
        for (const reward of response.rewards) {
          const record = toCachedLoyaltyRewardRecord(reward, cachedAt);
          rewards[record.id] = record;
          recentRewardIds.push(record.id);
        }
        const cache: LoyaltyRewardsCache = {
          rewards,
          recentRewardIds: recentRewardIds.slice(0, MAX_CACHED_LOYALTY_REWARDS),
        };
        return cache;
      },
      (data) => Object.keys(data.rewards).length,
      errors,
    );
    if (loyaltyRewardsSection) sections.push(loyaltyRewardsSection);
  } else {
    const existingEligibleCoupons =
      await getCachedReferenceData<EligibleCouponsCache>(
        branchId,
        ELIGIBLE_COUPONS_SECTION,
      );
    if (existingEligibleCoupons) {
      sections.push({
        key: referenceCacheKey(branchId, ELIGIBLE_COUPONS_SECTION),
        data: existingEligibleCoupons,
        count: existingEligibleCoupons.coupons.length,
      });
    }

    const existingLoyaltyRewards =
      await getCachedReferenceData<LoyaltyRewardsCache>(
        branchId,
        LOYALTY_REWARDS_SECTION,
      );
    if (existingLoyaltyRewards) {
      sections.push({
        key: referenceCacheKey(branchId, LOYALTY_REWARDS_SECTION),
        data: existingLoyaltyRewards,
        count: Object.keys(existingLoyaltyRewards.rewards).length,
      });
    }
  }

  if (includeVariantDetails) {
    const productsSection = sections.find((section) =>
      section.key.endsWith(":products"),
    );
    const products: Array<{ id: string }> =
      productsSection && Array.isArray(productsSection.data)
        ? (productsSection.data as Array<{ id: string }>)
        : [];
    const productIds = products.map((product) => product.id);
    const variantDetails = await captureSection(
      referenceCacheKey(branchId, "variant-details"),
      () => loadVariantDetails(branchId, productIds),
      (data) =>
        Object.keys(data.productVariants).length +
        Object.keys(data.productOptions).length +
        Object.keys(data.variantModifiers).length,
      errors,
    );
    if (variantDetails) sections.push(variantDetails);
  }

  const counts = sections.reduce<Record<string, number>>((acc, section) => {
    acc[section.key] = section.count;
    return acc;
  }, {});

  const manifest: ReferenceCacheManifest = {
    branchId,
    deviceId,
    refreshedAt: cachedAt,
    cacheVersion: CACHE_VERSION,
    keys: sections.map((section) => section.key),
    counts,
    errors,
  };

  const records: ReferenceCacheRecord[] = [
    ...sections.map((section) => ({
      key: section.key,
      branchId,
      deviceId,
      data: section.data,
      cacheVersion: CACHE_VERSION,
      cachedAt,
      expiresAt,
    })),
    {
      key: referenceCacheManifestKey(branchId),
      branchId,
      deviceId,
      data: manifest,
      cacheVersion: CACHE_VERSION,
      cachedAt,
      expiresAt,
    },
  ];

  await saveReferenceCacheBatch(records);
  return manifest;
}

function normalizeCustomerPhoneForCache(phone: string): string {
  return phone.trim().replace(/\s+/g, "");
}

function emptyCustomersByPhoneCache(): CustomersByPhoneCache {
  return { customers: {}, recentPhones: [] };
}

async function loadCustomersByPhoneCache(
  branchId: string,
): Promise<CustomersByPhoneCache> {
  return (
    (await getCachedReferenceData<CustomersByPhoneCache>(
      branchId,
      CUSTOMERS_BY_PHONE_SECTION,
    )) ?? emptyCustomersByPhoneCache()
  );
}

export async function getCachedCustomerByPhone(
  branchId: string,
  phoneInput: string,
): Promise<CachedCustomerRecord | undefined> {
  const phone = normalizeCustomerPhoneForCache(phoneInput);
  if (!phone) return undefined;

  const cache = await loadCustomersByPhoneCache(branchId);
  const existing = cache.customers[phone];
  if (!existing) return undefined;

  const now = new Date().toISOString();
  const nextCache: CustomersByPhoneCache = {
    customers: {
      ...cache.customers,
      [phone]: {
        ...existing,
        lastUsedAt: now,
      },
    },
    recentPhones: [
      phone,
      ...cache.recentPhones.filter((entry) => entry !== phone),
    ],
  };

  await saveReferenceCacheSection(
    branchId,
    CUSTOMERS_BY_PHONE_SECTION,
    nextCache,
  );
  return nextCache.customers[phone];
}

export async function saveCustomerToReferenceCache(
  branchId: string,
  input: {
    customerId: string;
    phone: string;
    firstName?: string;
    lastName?: string;
    loyaltyPoints?: number;
  },
  options: CacheFirstOptions = {},
): Promise<CachedCustomerRecord> {
  const phone = normalizeCustomerPhoneForCache(input.phone);
  if (!phone) {
    throw new Error("Customer phone is required");
  }

  const cache = await loadCustomersByPhoneCache(branchId);
  const now = new Date().toISOString();
  const existing = cache.customers[phone];
  const record: CachedCustomerRecord = {
    customerId: input.customerId,
    phone,
    firstName: input.firstName ?? existing?.firstName,
    lastName: input.lastName ?? existing?.lastName,
    loyaltyPoints: input.loyaltyPoints ?? existing?.loyaltyPoints,
    cachedAt: existing?.cachedAt ?? now,
    lastUsedAt: now,
  };

  const recentPhones = [
    phone,
    ...cache.recentPhones.filter((entry) => entry !== phone),
  ].slice(0, MAX_CACHED_CUSTOMERS);

  const customers: Record<string, CachedCustomerRecord> = {
    ...cache.customers,
    [phone]: record,
  };

  for (const stalePhone of Object.keys(customers)) {
    if (!recentPhones.includes(stalePhone)) {
      delete customers[stalePhone];
    }
  }

  await saveReferenceCacheSection(
    branchId,
    CUSTOMERS_BY_PHONE_SECTION,
    { customers, recentPhones },
    options,
  );
  return record;
}

export async function getCustomerReferenceCacheCount(
  branchId: string,
): Promise<number> {
  const cache = await loadCustomersByPhoneCache(branchId);
  return Object.keys(cache.customers).length;
}

function normalizeCouponCodeForCache(code: string): string {
  return code.trim().toUpperCase();
}

function emptyCouponRulesCache(): CouponRulesCache {
  return { rules: {}, recentCodes: [] };
}

async function loadCouponRulesCache(
  branchId: string,
): Promise<CouponRulesCache> {
  return (
    (await getCachedReferenceData<CouponRulesCache>(
      branchId,
      COUPON_RULES_SECTION,
    )) ?? emptyCouponRulesCache()
  );
}

async function mergeCouponRulesIntoCache(
  branchId: string,
  coupons: ServerCouponLike[],
  options: CacheFirstOptions = {},
): Promise<void> {
  if (coupons.length === 0) return;

  const cache = await loadCouponRulesCache(branchId);
  const now = new Date().toISOString();
  const rules = { ...cache.rules };
  const touchedCodes: string[] = [];

  for (const coupon of coupons) {
    const code = normalizeCouponCodeForCache(coupon.code);
    if (!code) continue;
    const existing = rules[code];
    rules[code] = {
      ...toCachedCouponRuleRecord(coupon, existing?.cachedAt ?? now),
      lastUsedAt: now,
    };
    touchedCodes.push(code);
  }

  const recentCodes = [
    ...touchedCodes,
    ...cache.recentCodes.filter((entry) => !touchedCodes.includes(entry)),
  ].slice(0, MAX_CACHED_COUPON_RULES);

  const nextRules: Record<string, CachedCouponRuleRecord> = {};
  for (const code of recentCodes) {
    const rule = rules[code];
    if (rule) nextRules[code] = rule;
  }

  await saveReferenceCacheSection(
    branchId,
    COUPON_RULES_SECTION,
    { rules: nextRules, recentCodes },
    options,
  );
}

export async function saveCouponRuleToReferenceCache(
  branchId: string,
  coupon: ServerCouponLike,
  options: CacheFirstOptions = {},
): Promise<CachedCouponRuleRecord> {
  await mergeCouponRulesIntoCache(branchId, [coupon], options);
  const code = normalizeCouponCodeForCache(coupon.code);
  const cache = await loadCouponRulesCache(branchId);
  const record = cache.rules[code];
  if (!record) {
    throw new Error("Failed to save coupon rule to cache");
  }
  return record;
}

export async function getCachedCouponRuleByCode(
  branchId: string,
  codeInput: string,
): Promise<CachedCouponRuleRecord | undefined> {
  const code = normalizeCouponCodeForCache(codeInput);
  if (!code) return undefined;

  const cache = await loadCouponRulesCache(branchId);
  const existing = cache.rules[code];
  if (!existing) return undefined;

  const now = new Date().toISOString();
  const nextCache: CouponRulesCache = {
    rules: {
      ...cache.rules,
      [code]: {
        ...existing,
        lastUsedAt: now,
      },
    },
    recentCodes: [code, ...cache.recentCodes.filter((entry) => entry !== code)],
  };

  await saveReferenceCacheSection(branchId, COUPON_RULES_SECTION, nextCache);
  return nextCache.rules[code];
}

export async function getCachedEligibleCoupons(
  branchId: string,
): Promise<EligibleCouponsCache | undefined> {
  return await getCachedReferenceData<EligibleCouponsCache>(
    branchId,
    ELIGIBLE_COUPONS_SECTION,
  );
}

function filterEligibleCouponsLocally(input: {
  coupons: CachedEligibleCouponRecord[];
  customerId?: string;
  orderSubtotal: number;
  search?: string;
  page: number;
  pageSize: number;
}): { items: CachedEligibleCouponRecord[]; nextPage: number | null } {
  const q = input.search?.trim().toLowerCase() ?? "";
  const filtered = input.coupons.filter((coupon) => {
    if (q && !coupon.code.toLowerCase().includes(q)) return false;
    if (coupon.customerId && coupon.customerId !== input.customerId) {
      return false;
    }
    return true;
  });

  const itemsWithDiscount = filtered.map((coupon) => ({
    ...coupon,
    calculatedDiscount: calculateCouponDiscountFromSnapshot(
      coupon,
      input.orderSubtotal,
    ),
  }));

  const start = (input.page - 1) * input.pageSize;
  const items = itemsWithDiscount.slice(start, start + input.pageSize);
  const hasMore = start + items.length < itemsWithDiscount.length;
  return { items, nextPage: hasMore ? input.page + 1 : null };
}

export async function getCachedEligibleCouponsPaged(input: {
  branchId: string;
  customerId?: string;
  orderSubtotal: number;
  search?: string;
  page: number;
  pageSize?: number;
}): Promise<{ items: CachedEligibleCouponRecord[]; nextPage: number | null }> {
  const pageSize = Math.min(50, Math.max(1, input.pageSize ?? 20));
  const eligibleCache = await getCachedEligibleCoupons(input.branchId);
  const coupons = eligibleCache?.coupons ?? [];

  if (coupons.length > 0) {
    return filterEligibleCouponsLocally({
      coupons,
      customerId: input.customerId,
      orderSubtotal: input.orderSubtotal,
      search: input.search,
      page: input.page,
      pageSize,
    });
  }

  const rulesCache = await loadCouponRulesCache(input.branchId);
  const fallbackCoupons = Object.values(rulesCache.rules).map((rule) => ({
    ...rule,
    calculatedDiscount: calculateCouponDiscountFromSnapshot(
      rule,
      input.orderSubtotal,
    ),
  }));

  return filterEligibleCouponsLocally({
    coupons: fallbackCoupons,
    customerId: input.customerId,
    orderSubtotal: input.orderSubtotal,
    search: input.search,
    page: input.page,
    pageSize,
  });
}

export async function getCouponReferenceCacheCount(
  branchId: string,
): Promise<number> {
  const [rulesCache, eligibleCache] = await Promise.all([
    loadCouponRulesCache(branchId),
    getCachedEligibleCoupons(branchId),
  ]);
  return Math.max(
    Object.keys(rulesCache.rules).length,
    eligibleCache?.coupons.length ?? 0,
  );
}

export async function hasCouponReferenceCache(
  branchId: string,
): Promise<boolean> {
  return (await getCouponReferenceCacheCount(branchId)) > 0;
}

function emptyLoyaltyRewardsCache(): LoyaltyRewardsCache {
  return { rewards: {}, recentRewardIds: [] };
}

function toCachedLoyaltyRewardRecord(
  reward: {
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
    rewardType: string;
    pointsCost: number;
    fulfillment: unknown;
    sortOrder: string;
  },
  cachedAt: string,
): CachedLoyaltyRewardRecord {
  const rewardType =
    reward.rewardType === "FREE_PRODUCT"
      ? "FREE_PRODUCT"
      : reward.rewardType === "ORDER_DISCOUNT_PERCENT"
        ? "ORDER_DISCOUNT_PERCENT"
        : "ORDER_DISCOUNT_AMOUNT";
  return {
    id: reward.id,
    name: reward.name,
    description: reward.description,
    imageUrl: reward.imageUrl,
    rewardType,
    pointsCost: Number(reward.pointsCost),
    fulfillment: reward.fulfillment,
    active: true,
    validFrom: null,
    validTo: null,
    maxRedemptionsTotal: null,
    maxRedemptionsPerCustomer: null,
    sortOrder: reward.sortOrder,
    cachedAt,
    lastUsedAt: cachedAt,
  };
}

async function loadLoyaltyRewardsCache(
  branchId: string,
): Promise<LoyaltyRewardsCache> {
  return (
    (await getCachedReferenceData<LoyaltyRewardsCache>(
      branchId,
      LOYALTY_REWARDS_SECTION,
    )) ?? emptyLoyaltyRewardsCache()
  );
}

export async function getCachedLoyaltyRewardById(
  branchId: string,
  rewardId: string,
): Promise<CachedLoyaltyRewardRecord | undefined> {
  const cache = await loadLoyaltyRewardsCache(branchId);
  return cache.rewards[rewardId];
}

type LoyaltyRewardCacheInput = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  rewardType: string;
  pointsCost: number;
  fulfillment: unknown;
  sortOrder: string;
};

/** ບັນທຶກຫຼາຍລາງວັນຄັ້ງດຽວ — ກັນ loop notify + reload ຫນ້າ local order */
export async function saveLoyaltyRewardsBatchToReferenceCache(
  branchId: string,
  rewards: LoyaltyRewardCacheInput[],
): Promise<void> {
  if (rewards.length === 0) return;

  const cache = await loadLoyaltyRewardsCache(branchId);
  const now = new Date().toISOString();
  const nextRewards = { ...cache.rewards };
  const newIds: string[] = [];

  for (const reward of rewards) {
    const record = toCachedLoyaltyRewardRecord(reward, now);
    nextRewards[record.id] = record;
    newIds.push(record.id);
  }

  const nextCache: LoyaltyRewardsCache = {
    rewards: nextRewards,
    recentRewardIds: [
      ...newIds,
      ...cache.recentRewardIds.filter((id) => !newIds.includes(id)),
    ].slice(0, MAX_CACHED_LOYALTY_REWARDS),
  };
  await saveReferenceCacheSection(branchId, LOYALTY_REWARDS_SECTION, nextCache);
}

export async function saveLoyaltyRewardToReferenceCache(
  branchId: string,
  reward: LoyaltyRewardCacheInput,
): Promise<CachedLoyaltyRewardRecord> {
  const now = new Date().toISOString();
  const record = toCachedLoyaltyRewardRecord(reward, now);
  await saveLoyaltyRewardsBatchToReferenceCache(branchId, [reward]);
  return record;
}

export async function getCachedAffordableLoyaltyRewards(input: {
  branchId: string;
  effectiveBalance: number;
}): Promise<
  Array<{
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
    rewardType: string;
    pointsCost: number;
    fulfillment: unknown;
    sortOrder: string;
  }>
> {
  const cache = await loadLoyaltyRewardsCache(input.branchId);
  const at = new Date().toISOString();
  const eligible: Array<{
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
    rewardType: string;
    pointsCost: number;
    fulfillment: unknown;
    sortOrder: string;
  }> = [];

  const rewardIds = [
    ...new Set([...cache.recentRewardIds, ...Object.keys(cache.rewards)]),
  ];

  for (const id of rewardIds) {
    const reward = cache.rewards[id];
    if (!reward) continue;
    if (
      !isAffordableLoyaltyRewardFromCache({
        reward,
        effectiveBalance: input.effectiveBalance,
        at,
      })
    ) {
      continue;
    }
    eligible.push({
      id: reward.id,
      name: reward.name,
      description: reward.description,
      imageUrl: reward.imageUrl,
      rewardType: reward.rewardType,
      pointsCost: reward.pointsCost,
      fulfillment: reward.fulfillment,
      sortOrder: reward.sortOrder,
    });
  }

  return eligible.sort((a, b) => a.sortOrder.localeCompare(b.sortOrder));
}

export async function getLoyaltyRewardsReferenceCacheCount(
  branchId: string,
): Promise<number> {
  const cache = await loadLoyaltyRewardsCache(branchId);
  return Object.keys(cache.rewards).length;
}

export async function hasLoyaltyRewardsReferenceCache(
  branchId: string,
): Promise<boolean> {
  return (await getLoyaltyRewardsReferenceCacheCount(branchId)) > 0;
}

export async function getCachedCustomerByCustomerId(
  branchId: string,
  customerId: string,
): Promise<CachedCustomerRecord | undefined> {
  const cache = await loadCustomersByPhoneCache(branchId);
  return Object.values(cache.customers).find(
    (c) => c.customerId === customerId,
  );
}
